import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import RecoveryCodeModal from "../components/RecoveryCodeModal";
import { api, formatApiErrorDetail } from "../lib/api";

function formatCode(raw) {
  const cleaned = (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  let body = cleaned;
  if (body.startsWith("TJ")) body = body.slice(2);
  body = body.slice(0, 12);
  const g1 = body.slice(0, 4);
  const g2 = body.slice(4, 8);
  const g3 = body.slice(8, 12);
  const groups = [g1, g2, g3].filter(Boolean);
  return "TJ" + (groups.length ? "-" + groups.join("-") : "");
}

export default function PasswordReset() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState("code");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="password-reset-page">
      <Toaster richColors position="top-right" />
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 py-12">
        <Link to="/" className="mb-6 flex items-center gap-2 text-slate-900">
          <img src="/Logo.png" alt="Talent Junction" className="h-14 w-14 rounded-xl object-cover" />
          <span className="text-base font-semibold tracking-tight">{t("brand")}</span>
        </Link>
        <div className="w-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">{t("password_reset.eyebrow")}</p>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">{t("password_reset.title")}</h1>

          {/* Tabs */}
          <div className="mt-6 flex gap-1 border-b border-slate-200" data-testid="password-reset-tabs">
            <button
              onClick={() => setTab("code")}
              data-testid="password-reset-tab-code"
              className={
                "-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition-colors " +
                (tab === "code"
                  ? "border-indigo-600 text-indigo-700"
                  : "border-transparent text-slate-500 hover:text-slate-700")
              }
            >
              {t("password_reset.tab_code")}
            </button>
            <button
              onClick={() => setTab("email")}
              data-testid="password-reset-tab-email"
              className={
                "-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition-colors " +
                (tab === "email"
                  ? "border-indigo-600 text-indigo-700"
                  : "border-transparent text-slate-500 hover:text-slate-700")
              }
            >
              {t("password_reset.tab_email")}
            </button>
          </div>

          {tab === "code" ? <CodeForm onDone={() => navigate("/login")} /> : <EmailForm />}

          <div className="mt-6 text-sm">
            <Link to="/login" className="font-medium text-indigo-700 hover:text-indigo-800" data-testid="password-reset-back-login">
              {t("password_reset.back_to_login")}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

function CodeForm({ onDone }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("TJ-");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [newCode, setNewCode] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== pw2) {
      setError(t("password_reset.err_mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/password-reset", { email, recovery_code: code, new_password: pw });
      setNewCode(data.recovery_code);
    } catch (err) {
      if (err.response?.status === 429) setError(t("password_reset.err_rate_limit"));
      else setError(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setSubmitting(false); }
  };

  const finish = () => {
    setNewCode(null);
    toast.success(t("password_reset.success_toast"));
    onDone();
  };

  return (
    <>
      <p className="mt-4 text-sm text-slate-600">{t("password_reset.subtitle")}</p>
      <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="password-reset-form">
        <Field label={t("password_reset.email")} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required testId="password-reset-email" />
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("password_reset.code")}</label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(formatCode(e.target.value))}
            placeholder="TJ-XXXX-XXXX-XXXX"
            required
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            data-testid="password-reset-code"
          />
        </div>
        <Field label={t("password_reset.new_password")} type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} testId="password-reset-new" />
        <Field label={t("password_reset.confirm_password")} type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8} testId="password-reset-confirm" />
        {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="password-reset-error">{error}</div>}
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          data-testid="password-reset-submit"
        >
          {submitting ? t("common.loading") : t("password_reset.submit")}
        </button>
      </form>
      {newCode && <RecoveryCodeModal code={newCode} variant="reset" onClose={finish} />}
    </>
  );
}

function EmailForm() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset-request", { email });
      setDone(true);
    } catch (_) {
      setDone(true); // Always show generic message
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800" data-testid="password-reset-email-success">
        {t("password_reset.email_generic_success")}
      </div>
    );
  }
  return (
    <>
      <p className="mt-4 text-sm text-slate-600">{t("password_reset.email_subtitle")}</p>
      <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="password-reset-email-form">
        <Field label={t("password_reset.email")} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required testId="password-reset-email-input" />
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          data-testid="password-reset-email-submit"
        >
          {submitting ? t("common.loading") : t("password_reset.email_submit")}
        </button>
      </form>
    </>
  );
}

function Field({ label, value, onChange, type = "text", required, minLength, testId }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        minLength={minLength}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
        data-testid={testId}
      />
    </div>
  );
}
